import { useRef, useEffect, useCallback, useState } from "react";

const DRAG_THRESHOLD = 5; // px — movement below this is a click, not a drag
const RAMP_MS = 600; // ease-in duration when auto-scroll resumes
const INTERACTION_IDLE_MS = 1500; // idle time after drag/wheel before auto-scroll resumes

// Configurable speed constants (px/s)
export const MARQUEE_SPEED_ROW1 = 60;
export const MARQUEE_SPEED_ROW2 = 45;

interface UseMarqueeOptions {
  /** Pixels per second. Positive = scroll left, negative = scroll right */
  speed: number;
  /** Whether the marquee is active */
  enabled?: boolean;
  /** Pass card count so hook re-measures when content changes */
  cardCount?: number;
  /** External pause (hover / explicit pause toggle). Loop stays alive. */
  paused?: boolean;
}

/**
 * RAF-driven seamless marquee with drag/swipe support.
 *
 * Attach `trackRef` to the flex container that holds 2× the card list.
 * Attach `containerRef` to the overflow-hidden wrapper.
 * Spread `handlers` onto the container element.
 *
 * `ready` is true once the track has been measured successfully (halfWidth > 0).
 * When `ready` is false, the consumer should render a fallback (e.g. overflow-x-auto).
 */
export function useMarquee({ speed, enabled = true, cardCount = 0, paused = false }: UseMarqueeOptions) {
  const containerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  // `ready` uses useState so the component re-renders when measurement succeeds
  const [ready, setReady] = useState(false);

  // Mutable state kept outside React state to avoid re-renders in RAF
  const state = useRef({
    offset: 0,
    halfWidth: 0,
    lastTime: 0,
    animating: true,
    rafId: 0,
    timedOut: false,
    readyLogged: false,
    // drag state
    pointerDown: false,
    dragging: false,
    dragMoved: false,
    dragStartX: 0,
    dragStartOffset: 0,
    resumeTimer: 0 as ReturnType<typeof setTimeout> | 0,
    // pause + ramp state
    paused: false,
    /** Timestamp (ms) until which user interaction suppresses auto-scroll */
    suppressUntil: 0,
    /** 0→1 ease-in multiplier applied to speed when motion resumes */
    ramp: 0,
  });

  // Mirror the external `paused` prop into the RAF-readable ref
  useEffect(() => {
    state.current.paused = paused;
    if (paused) state.current.ramp = 0;
  }, [paused]);

  /** Wrap an offset into [0, halfWidth) */
  const wrapOffset = useCallback((value: number, halfWidth: number) => {
    if (halfWidth <= 0) return value;
    return ((value % halfWidth) + halfWidth) % halfWidth;
  }, []);


  // Measure half-width (the width of one copy of the cards)
  const measure = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    // The repeat distance is the offset of the clone set, not scrollWidth/2:
    // the flex `gap` between the original set and the clone wrapper makes the
    // two halves unequal, which produces a visible snap on every wrap.
    const clone = track.querySelector<HTMLElement>("[data-marquee-clone]");
    const sw = clone && clone.offsetLeft > 0 ? clone.offsetLeft * 2 : track.scrollWidth;
    if (sw > 0) {
      const hw = sw / 2;
      state.current.halfWidth = hw;
      state.current.readyLogged = true;


      setReady(true);

      // For reverse direction, initialise offset at halfWidth
      if (speed < 0 && state.current.offset === 0) {
        state.current.offset = hw;
      }

      // Content width can change (images loading, resize). Keep the offset
      // inside [0, halfWidth) so the track never scrolls past its content.
      const o = state.current.offset;
      state.current.offset = ((o % hw) + hw) % hw;
    }
  }, [speed]);

  // Core animation loop
  const tick = useCallback(
    (now: number) => {
      const s = state.current;
      const track = trackRef.current;

      // Suspended: paused (hover / toggle), dragging, or inside the post-
      // interaction idle window. Keep the loop alive and reset the ramp so
      // motion eases back in rather than snapping.
      const suspended = s.paused || s.dragging || now < s.suppressUntil;

      if (!track || !s.animating || suspended || s.halfWidth <= 0) {
        if (suspended) s.ramp = 0;
        s.lastTime = now;
        s.rafId = requestAnimationFrame(tick);
        return;
      }

      const dt = (now - s.lastTime) / 1000;
      s.lastTime = now;

      // Cap dt to avoid huge jumps on tab-switch
      const clampedDt = Math.min(dt, 0.1);

      // Ease-in: ramp 0→1 over RAMP_MS with an ease-out curve
      s.ramp = Math.min(1, s.ramp + (clampedDt * 1000) / RAMP_MS);
      const eased = 1 - Math.pow(1 - s.ramp, 3);

      s.offset += speed * eased * clampedDt;

      // Seamless wrap
      if (speed > 0) {
        if (s.offset >= s.halfWidth) s.offset -= s.halfWidth;
      } else {
        if (s.offset <= 0) s.offset += s.halfWidth;
      }

      track.style.transform = `translate3d(${-s.offset}px, 0, 0)`;
      s.rafId = requestAnimationFrame(tick);
    },
    [speed],
  );


  // Start / stop loop + ResizeObserver + safety timeout
  useEffect(() => {
    if (!enabled) return;

    const s = state.current;
    s.animating = true;
    s.lastTime = performance.now();

    // Double-rAF measurement on mount (safety net for first paint)
    const doubleRaf = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        measure();
      });
    });

    // ResizeObserver — fires when track first gains dimensions and on resize
    let ro: ResizeObserver | null = null;
    const track = trackRef.current;
    if (track && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => {
        measure();
      });
      ro.observe(track);
    }

    // Safety timeout: if ready hasn't fired after 1500ms, stay in fallback
    const timeout = setTimeout(() => {
      if (s.halfWidth <= 0) {
        // Keep fallback mode (overflow-x-auto) — don't setReady(true)
        s.timedOut = true;

      }
    }, 1500);

    // Start RAF loop
    s.rafId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(s.rafId);
      cancelAnimationFrame(doubleRaf);
      clearTimeout(timeout);
      if (ro) ro.disconnect();
    };
  }, [enabled, tick, measure]);

  // Force re-measure when card count changes
  useEffect(() => {
    if (cardCount > 0) {
      state.current.halfWidth = 0;
      setReady(false);
      state.current.readyLogged = false;
      // Double-rAF to measure after paint
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          measure();
        });
      });
    }
  }, [cardCount, measure]);

  // Horizontal wheel / trackpad scrubbing.
  // Registered natively (non-passive) because React's synthetic onWheel is
  // passive and cannot preventDefault the browser's own scroll.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !enabled) return;

    const onWheel = (e: WheelEvent) => {
      // Leave vertical scrolling to the page
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;

      const s = state.current;
      if (s.halfWidth <= 0) return;

      e.preventDefault();

      s.offset = wrapOffset(s.offset + e.deltaX, s.halfWidth);
      if (trackRef.current) {
        trackRef.current.style.transform = `translate3d(${-s.offset}px, 0, 0)`;
      }

      // Treat as interaction: hold auto-scroll, then ease back in
      s.suppressUntil = performance.now() + INTERACTION_IDLE_MS;
      s.ramp = 0;
    };

    container.addEventListener("wheel", onWheel, { passive: false });
    return () => container.removeEventListener("wheel", onWheel);
  }, [enabled, wrapOffset]);



  // Pointer / touch handlers for drag with click-vs-drag threshold
  const onPointerDown = useCallback((e: React.PointerEvent) => {
    const s = state.current;
    s.pointerDown = true;
    s.dragging = false;
    s.dragMoved = false;
    s.dragStartX = e.clientX;
    s.dragStartOffset = s.offset;
    if (s.resumeTimer) clearTimeout(s.resumeTimer);
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    const s = state.current;
    if (!s.pointerDown) return;

    const dx = e.clientX - s.dragStartX;

    // Activate drag only after exceeding threshold
    if (!s.dragging) {
      if (Math.abs(dx) < DRAG_THRESHOLD) return;
      s.dragging = true;
      s.dragMoved = true;
      // Capture pointer only once we know it's a real drag
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    }

    let newOffset = s.dragStartOffset - dx;

    if (s.halfWidth > 0) {
      while (newOffset < 0) newOffset += s.halfWidth;
      while (newOffset >= s.halfWidth) newOffset -= s.halfWidth;
    }

    s.offset = newOffset;
    if (trackRef.current) {
      trackRef.current.style.transform = `translate3d(${-s.offset}px, 0, 0)`;
    }
  }, []);

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    const s = state.current;
    const wasDrag = s.dragMoved;
    s.pointerDown = false;
    s.dragging = false;

    if (wasDrag) {
      try {
        (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
      } catch {
        // Pointer capture may not have been set
      }

      // Suppress the click event that fires after a real drag
      const container = containerRef.current;
      if (container) {
        const suppressClick = (evt: Event) => {
          evt.preventDefault();
          evt.stopPropagation();
          container.removeEventListener("click", suppressClick, true);
        };
        container.addEventListener("click", suppressClick, true);
        // Safety: remove listener if click never fires
        setTimeout(() => container.removeEventListener("click", suppressClick, true), 100);
      }
    }

    // Hold auto-scroll briefly after a real drag, then ease back in
    if (wasDrag) {
      s.suppressUntil = performance.now() + INTERACTION_IDLE_MS;
      s.ramp = 0;
    }

    s.resumeTimer = setTimeout(() => {
      s.lastTime = performance.now();
    }, INTERACTION_IDLE_MS);
  }, []);

  /**
   * Scroll a descendant of the track into the visible window of the container.
   *
   * The track is positioned with `translate3d` inside an `overflow-hidden`
   * container, so the browser cannot scroll a keyboard-focused card into view
   * on its own -- focus would land on a card the user cannot see (WCAG 2.4.11).
   * We nudge the marquee offset instead. The loop runs a few times because
   * wrapping the offset into [0, halfWidth) can move the target again.
   */
  const revealElement = useCallback(
    (el: HTMLElement | null) => {
      const container = containerRef.current;
      const track = trackRef.current;
      const s = state.current;
      if (!el || !container || !track || s.halfWidth <= 0) return;

      const margin = 24;

      for (let attempt = 0; attempt < 4; attempt++) {
        const cRect = container.getBoundingClientRect();
        const eRect = el.getBoundingClientRect();

        let delta = 0;
        if (eRect.left < cRect.left + margin) {
          delta = eRect.left - (cRect.left + margin);
        } else if (eRect.right > cRect.right - margin) {
          delta = eRect.right - (cRect.right - margin);
        }
        if (Math.abs(delta) < 1) return;

        s.offset = wrapOffset(s.offset + delta, s.halfWidth);
        track.style.transform = `translate3d(${-s.offset}px, 0, 0)`;
      }
    },
    [wrapOffset],
  );

  const handlers = {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel: onPointerUp,
  };

  return { containerRef, trackRef, handlers, ready, revealElement };
}

